import { auth } from '../firebase/config';

export type InviteStatus = 'active' | 'provisioning' | 'provisioning_failed' | 'used' | 'revoked';

export interface BackendInvite {
  id: string;
  code?: string;
  unitId: string;
  role: string;
  position?: string;
  status: InviteStatus;
  mode?: 'create' | 'claim_existing';
  email?: string;
  prefilledLastName?: string;
  prefilledFirstName?: string;
  existingUserId?: string;
  createdAt?: string;
  expiresAt?: string;
  usedBy?: string;
  usedAt?: string;
}

export interface CreateInvitePayload {
  unitId: string;
  role: string;
  position?: string;
  email?: string;
  prefilledLastName?: string;
  prefilledFirstName?: string;
  mode?: 'create' | 'claim_existing';
  existingUserId?: string;
  expiresAt?: string;
}

export interface PublicConsumeInvite {
  role: string;
  unitId: string;
  position?: string;
  mode?: 'create' | 'claim_existing';
  expiresAt?: string;
}

export interface ConsumeInvitePayload {
  code: string;
  registrationData: {
    email: string;
    password?: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface RegisterInvitePayload {
  code: string;
  registrationData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    username: string;
  };
}

const BACKEND_BASE_URL =
  import.meta.env.VITE_BACKEND_BASE_URL ||
  import.meta.env.VITE_FUNCTIONS_BASE_URL ||
  'https://mintleaf-foglalasok-4-ai-copy.onrender.com';

const getAuthToken = async (): Promise<string> => {
  const sessionToken = window.localStorage.getItem('mintleaf_api_token');
  if (sessionToken) {
    return sessionToken;
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('UNAUTHENTICATED');
  }

  return user.getIdToken();
};

const parseResponseOrThrow = async (response: Response) => {
  if (!response.ok) {
    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    const error = new Error(payload?.message || 'INVITES_API_ERROR');
    (error as any).statusCode = response.status;
    (error as any).details = payload?.errors || [];
    throw error;
  }

  return response.json();
};

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = await getAuthToken();
  const response = await fetch(`${BACKEND_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });

  return parseResponseOrThrow(response) as Promise<T>;
};

const publicRequest = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(`${BACKEND_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });

  return parseResponseOrThrow(response) as Promise<T>;
};

export const listInvites = async (unitId: string): Promise<BackendInvite[]> => {
  const query = new URLSearchParams({ unitId });
  return request<BackendInvite[]>(`/api/invites?${query.toString()}`);
};

export const createInvite = async (payload: CreateInvitePayload): Promise<BackendInvite> =>
  request<BackendInvite>('/api/invites', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const revokeInvite = async (inviteId: string): Promise<BackendInvite> =>
  request<BackendInvite>(`/api/invites/${encodeURIComponent(inviteId)}/revoke`, {
    method: 'POST',
  });

export const repairInviteOnboarding = async (inviteId: string): Promise<{ ok: true; inviteId: string; authUid: string; userId: string; unitId: string }> =>
  request<{ ok: true; inviteId: string; authUid: string; userId: string; unitId: string }>(`/api/invites/${encodeURIComponent(inviteId)}/repair-onboarding`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const getInviteForConsume = async (code: string): Promise<PublicConsumeInvite> =>
  publicRequest<PublicConsumeInvite>(`/api/invites/consume/${encodeURIComponent(code)}`);

export const consumeInvite = async (
  payload: ConsumeInvitePayload
): Promise<{ success: true; inviteId: string; userId?: string }> =>
  request<{ success: true; inviteId: string; userId?: string }>('/api/invites/consume', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export interface RegisterInviteResponse {
  success: boolean;
  inviteId?: string;
  // /api/invites/register contract: userId is the auth UID created/resolved for sign-in.
  // In claim_existing mode this is still the auth UID (not the canonical existingUserId).
  userId?: string;
  mode?: 'create' | 'claim_existing';
  idempotent?: boolean;
  message?: string;
}

export const registerInvite = async (
  payload: RegisterInvitePayload
): Promise<RegisterInviteResponse> =>
  publicRequest<RegisterInviteResponse>(
    '/api/invites/register',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
